import { SchemaFormSchema } from "components/Form/components/SchemaForm/SchemaForm.types";
import { RecordType } from "graphql/generated/enums";

/**
 * Form-dialect (form-*) schema that drives the no-code dashboard widget editor.
 *
 * Consumed by the dashboard resource config to render a `dashboard.value` as a
 * structured SchemaForm instead of a raw YAML code editor
 * (operator-task-dashboard-widget-editor-form). It models the DashboardConfig
 * shape from `src/containers/Dashboard/types.ts`
 * (`{version?, grid?, widgets?[]}`) and the per-type `config` field matrix
 * captured in operator-task-dashboard-value-schema.
 *
 * Discriminated union: `widgets.items` is a `form-dropdown` with
 * `objectIdentifier: "type"`. Each `oneOf` branch is a self-contained
 * `form-object` for exactly one of the 13 widget types registered in
 * `dashboardWidgetMapper.tsx`. The branch's `type` property is a
 * `form-const-string` whose `const` is the discriminant the dropdown matches on
 * (SchemaFormDropdown.tsx). Because the engine renders only the selected
 * branch's own `properties`, the common fields (uid/title/layout) are repeated
 * per branch — see the widgetBranch() helper below.
 *
 * The backend stores `dashboard.value` as opaque JSON and stays intentionally
 * permissive (dashboard.py / types.ts "so older viewers can open newer
 * configs"). This schema is editor-facing only; it does NOT enforce anything
 * server-side. Because the `oneOf` is closed over the 13 known types, a raw-edit
 * escape hatch must be preserved when wiring this into dashboard.tsx so a
 * dashboard authored by a newer FE (unknown widget `type`) is never dropped on
 * save.
 */

// --- Common widget fields (repeated per branch — engine renders only the
// selected branch's own properties) --------------------------------------
const uidField = {
    element: "form-string",
    type: "string",
    default: "",
    label: "UID",
    description:
        "A short unique key for this widget within the dashboard (any text, e.g. `open-execs`). It just identifies the widget in the config — it is **not** a technical or numeric id, and it is up to you to choose.",
};
const titleField = {
    element: "form-optional-string",
    type: "string",
    label: "Title",
    description: "Optional heading shown at the top of the widget.",
};
const layoutField = {
    element: "form-object",
    type: "object",
    additionalProperties: false,
    label: "Layout",
    description:
        "Where the widget sits on the grid and how many grid columns/rows it spans. Leave empty to auto-place. Set **Column span** to make a widget wider — e.g. `6` is half width on a 12-column grid. **Row span** sets the widget's height — how many grid rows tall it is (each row is the grid's **Row height (px)**).",
    properties: {
        col: { element: "form-optional-integer", type: "integer", label: "Column" },
        row: { element: "form-optional-integer", type: "integer", label: "Row" },
        // Spans are at least one grid cell — 0/negative would collapse the widget.
        // `minimum` is enforced by the integer form element (clamps out-of-range
        // input) so the form can't store an invalid span (Margot 2026-09-28).
        colSpan: { element: "form-optional-integer", type: "integer", default: 1, minimum: 1, label: "Column span" },
        rowSpan: { element: "form-optional-integer", type: "integer", default: 1, minimum: 1, label: "Row span" },
    },
};

// --- Reusable config-field elements --------------------------------------
// The fixed set of record types a widget can query — the canonical UPPERCASE
// `RecordType` values (the same StrEnum the backend and GraphQL use, e.g.
// `CUSTOM_OBJECT`). Stored widget configs carry these canonical values, so the
// dropdown option consts match them exactly (a case mismatch makes the
// discriminant fail to resolve and the field render "Invalid choice" —
// operator-task-fe-dashboard-widget-form-builder-fixes #1/#4). Widget renderers
// lowercase the value before building the REST/GraphQL query, so the canonical
// case is safe end-to-end. Typed as `[RecordType, string][]` so tsc rejects any
// value that is not a canonical record type (drift guard). Rendered as a CLOSED
// dropdown (not free text) so users pick a valid value instead of guessing; a
// wrong/empty record_type is the usual reason a widget "shows nothing"
// (operator-task-dashboard-widget-editor-form, Margot 2026-09-24). Common
// dashboard record types are listed first.
const RECORD_TYPE_OPTIONS: [RecordType, string][] = [
    ["EXECUTION", "Execution"],
    ["CUSTOM_OBJECT", "Custom object"],
    ["FLOW", "Flow"],
    ["CONNECTOR", "Connector"],
    ["SCHEDULE", "Schedule"],
    ["SCHEDULER", "Scheduler"],
    ["WEBHOOK", "Webhook"],
    ["FILE", "File"],
    ["PROJECT", "Project"],
    ["MESSAGE", "Message"],
    ["OBJECT_TEMPLATE", "Object template"],
    ["MCP_USER_TOOL", "MCP user tool"],
    ["BUNDLE", "Bundle"],
    ["BUNDLE_REPOSITORY", "Bundle repository"],
    ["PLUGIN", "Plugin"],
    ["PYTHON_MODULE", "Python module"],
    ["ROLE", "Role"],
    ["SECRET", "Secret"],
    ["SETTING", "Setting"],
    ["SYNC_CONFIG", "Sync config"],
    ["TAG", "Tag"],
    ["USER", "User"],
    ["VAULT_CONFIG", "Vault config"],
    ["WRAPPER", "Wrapper"],
    ["DASHBOARD", "Dashboard"],
    ["DEVOLUTIONS_CONFIG", "Devolutions config"],
    ["LDAP_CONFIG", "LDAP config"],
    ["ORGANIZATION", "Organization"],
    ["PROCESS", "Process"],
    ["SCHEMA", "Schema"],
    ["WORKSPACE", "Workspace"],
];
const recordTypeField = {
    element: "form-dropdown",
    label: "Record type",
    description:
        "The kind of record this widget shows (e.g. **Custom object**, **Execution**, **Flow**). Any **Filter** / **Order** / **Columns** you set must use the fields of this record type. For custom objects, set **Object template id** as well to narrow to one object-template.",
    defaultIndex: 0,
    anyOf: RECORD_TYPE_OPTIONS.map(([value, label]) => ({
        element: "form-const-string",
        type: "string",
        const: value,
        label,
    })),
};
// The record types a user can create from the main "Create" menu
// (config/resources.jsx: createResources + createMoreResources +
// configurationResources). The record_form widget POSTs a NEW record to
// /<record_type> on submit, so its Record type must be limited to types that can
// actually be created this way — otherwise picking a non-creatable type (e.g.
// Execution, Process) just errors on the create button (Margot 2026-09-28 #11,
// operator-task-dashboard-widget-editor-form). Kept as an explicit set rather
// than imported from resources.jsx to avoid a circular import
// (resources.jsx -> dashboard.tsx -> dashboardWidgetSchema.ts). If the Create
// menu's creatable set changes, update this list to match.
const CREATABLE_RECORD_TYPE_VALUES: RecordType[] = [
    "CUSTOM_OBJECT",
    "FLOW",
    "CONNECTOR",
    "PROJECT",
    "WEBHOOK",
    "FILE",
    "SCHEDULE",
    "SCHEDULER",
    "SCHEMA",
    "WRAPPER",
    "PYTHON_MODULE",
    "BUNDLE",
    "OBJECT_TEMPLATE",
    "SETTING",
    "DASHBOARD",
    "SYNC_CONFIG",
    "LDAP_CONFIG",
    "VAULT_CONFIG",
    "PLUGIN",
    "BUNDLE_REPOSITORY",
    "ROLE",
    "DEVOLUTIONS_CONFIG",
    "MCP_USER_TOOL",
    "SECRET",
];
const creatableRecordTypeField = {
    element: "form-dropdown",
    label: "Record type",
    description:
        "The kind of record this form creates. Limited to the record types you can create from the main **Create** menu — the widget submits a new record of this type when the form is filled in. For **Custom object**, also set **Object template** below.",
    defaultIndex: 0,
    anyOf: RECORD_TYPE_OPTIONS.filter(([value]) => CREATABLE_RECORD_TYPE_VALUES.includes(value)).map(
        ([value, label]) => ({
            element: "form-const-string",
            type: "string",
            const: value,
            label,
        }),
    ),
};
// The status_strip widget renders a provisioning-state badge per record and
// hardcodes the `custom_object_plain_list` query (StatusStripWidget.tsx) — it
// ignores `record_type` entirely and only works for CUSTOM_OBJECT. So instead of
// the full record-type dropdown, its Record type is restricted to a single locked
// "Custom object" option: the field stays visible to signal the widget's scope,
// but it can no longer be pointed at a record type that would break the widget
// (Margot 2026-09-28 #9, operator-task-dashboard-widget-editor-form).
const customObjectOnlyRecordTypeField = {
    element: "form-dropdown",
    label: "Record type",
    description:
        "Status strip shows the provisioning-state badge of **custom objects**, so it always operates on custom objects. Use **Filter** to narrow which custom objects appear.",
    defaultIndex: 0,
    anyOf: [{ element: "form-const-string", type: "string", const: "CUSTOM_OBJECT", label: "Custom object" }],
};
const filterField = {
    element: "form-record-filter",
    type: "object",
    record_type_field: "record_type",
    label: "Filter",
    description:
        "Restricts which records the widget shows. Build it with the filter controls, or switch to **Code view** to paste a filter copied from **Advanced search**.",
};
const orderField = {
    element: "form-optional-string",
    type: "string",
    label: "Order (e.g. -created_at)",
    description: "Field to sort by; prefix with `-` for descending, e.g. `-created_at`.",
};
const limitField = { element: "form-optional-integer", type: "integer", label: "Limit" };
const nField = { element: "form-optional-integer", type: "integer", label: "N (items per partition)" };
const liveField = { element: "form-optional-boolean", type: "boolean", label: "Live (auto-refresh)" };
// object_template_id stores an object-template record id. Render it as a
// record-reference autocomplete (resource "object_template", storing the id via
// store: "record_id") so the user picks a template by name instead of pasting a
// raw uuid. operator-task-fe-dashboard-widget-form-builder-fixes #4.
const objectTemplateIdField = {
    element: "form-optional-autocomplete",
    record_type: "object_template",
    store: "record_id",
    label: "Object template",
    description:
        "The object template to narrow to. Pick a template by name; its id is stored.",
    default: null,
    anyOf: [{ type: "string" }, { type: "null" }],
    // Object templates only apply to custom objects — show this field only when
    // the sibling Record type is Custom object, so it can't be set for record
    // types where it is meaningless (Margot 2026-09-28 #3, via the SchemaForm
    // `visibleWhen` conditional-visibility primitive).
    visibleWhen: { field: "record_type", value: "CUSTOM_OBJECT" },
};
const columnsField = {
    element: "form-optional-array",
    type: "array",
    label: "Columns",
    items: { element: "form-string", type: "string", default: "", label: "Column" },
};
// The record_form `fields` config is an array of field-DEFINITION objects
// (RecordFormFieldConfig in RecordFormWidget.tsx:
// {path,label,input,options,placeholder,required,default}) — NOT plain strings.
// Modeled as an array of form-objects so the editor renders a proper per-field
// sub-form; the old array-of-strings element rendered a single string input and
// showed "[object Object]" when a field object was pasted in (Margot 2026-09-28
// #11, operator-task-dashboard-widget-editor-form).
const recordFormFieldsField = {
    element: "form-optional-array",
    type: "array",
    label: "Fields",
    description:
        "The editable inputs shown in the form, top to bottom. Each field writes the entered value to a dotted **Path** in the created record (e.g. `name`, or `value.ask` for a custom-object attribute). When you pick a record type (and object template) while this list is empty, it is filled with the fields of the create-record screen: **Name** plus the object template's text and dropdown attributes, with required ones marked. Remove the ones you do not need. An empty list shows the same fields.",
    items: {
        element: "form-object",
        type: "object",
        additionalProperties: false,
        label: "Field",
        order: ["path", "label", "input", "required", "default", "placeholder", "options"],
        hidden: ["placeholder", "options"],
        required: ["path"],
        properties: {
            path: {
                element: "form-string",
                type: "string",
                default: "",
                label: "Path",
                description:
                    "Where the entered value is written in the created record. Use a bare key for a top-level field (e.g. `name`), or a `value.` prefix for a custom-object attribute (e.g. `value.ask`).",
            },
            label: {
                element: "form-optional-string",
                type: "string",
                label: "Label",
                description: "Text shown above the input. Defaults to the last segment of the path.",
            },
            input: {
                element: "form-dropdown",
                label: "Input",
                description: "Which control to render for this field.",
                defaultIndex: 0,
                anyOf: [
                    { element: "form-const-string", type: "string", const: "text", label: "Text" },
                    { element: "form-const-string", type: "string", const: "textarea", label: "Text area" },
                    { element: "form-const-string", type: "string", const: "select", label: "Select" },
                ],
            },
            required: { element: "form-optional-boolean", type: "boolean", label: "Required" },
            default: {
                element: "form-optional-string",
                type: "string",
                label: "Default value",
                description: "Value pre-filled into the field (and restored on reset).",
            },
            placeholder: {
                element: "form-optional-string",
                type: "string",
                label: "Placeholder",
                description: "Placeholder text shown in an empty text / text-area input.",
            },
            // Only relevant for a Select input — hidden entirely otherwise via the
            // SchemaForm visibleWhen conditional-visibility primitive.
            options: {
                element: "form-optional-array",
                type: "array",
                label: "Options",
                description: "The choices offered by a Select input.",
                visibleWhen: { field: "input", value: "select" },
                items: {
                    element: "form-object",
                    type: "object",
                    additionalProperties: false,
                    label: "Option",
                    order: ["label", "value"],
                    required: ["value"],
                    properties: {
                        label: {
                            element: "form-optional-string",
                            type: "string",
                            label: "Label",
                            description: "Text shown for this choice. Defaults to the value.",
                        },
                        value: { element: "form-string", type: "string", default: "", label: "Value" },
                    },
                },
            },
        },
    },
};
const partitionByField = {
    element: "form-object",
    type: "object",
    additionalProperties: false,
    label: "Partition by",
    properties: {
        field: { element: "form-string", type: "string", default: "", label: "Field" },
        limit: { element: "form-optional-integer", type: "integer", default: 1, label: "Limit per partition" },
        order: { element: "form-optional-string", type: "string", label: "Order" },
    },
};
const modeField = {
    element: "form-dropdown",
    label: "Mode",
    defaultIndex: 0,
    anyOf: [
        { element: "form-const-string", type: "string", const: "object_template", label: "Object template" },
        { element: "form-const-string", type: "string", const: "custom_object", label: "Custom object" },
    ],
};
const showField = {
    element: "form-dropdown",
    label: "Show",
    defaultIndex: 0,
    anyOf: [
        { element: "form-const-string", type: "string", const: "message", label: "Message" },
        { element: "form-const-string", type: "string", const: "output", label: "Output" },
        { element: "form-const-string", type: "string", const: "logs", label: "Logs" },
    ],
};
const navigateToExecutionField = {
    element: "form-optional-boolean",
    type: "boolean",
    label: "Navigate to execution",
};
const resetOnSuccessField = {
    element: "form-optional-boolean",
    type: "boolean",
    label: "Reset on success",
    description: "Clear the form after a successful submit. Defaults to on.",
};
const successMessageField = {
    element: "form-optional-string",
    type: "string",
    label: "Success message",
    description: "Notification text shown after a successful submit.",
};
const submitLabelField = {
    element: "form-optional-string",
    type: "string",
    label: "Submit label",
    description: "Text on the submit button (e.g. Create, Run).",
};
// flow_id stores a flow record id. Render it as a required record-reference
// autocomplete (form-autocomplete → Autocomplete, resource "flow") so the user
// picks a flow by name instead of pasting a raw uuid; the widget still stores
// the id (store: "record_id"). operator-task-fe-dashboard-widget-form-builder-fixes #3.
const flowIdField = {
    element: "form-autocomplete",
    type: "string",
    record_type: "flow",
    store: "record_id",
    default: "",
    label: "Flow",
    description: "The flow this widget runs — pick a flow by name; its id is stored.",
};

/**
 * Build one discriminated-union branch (a `form-object` for a single widget
 * type). The `type` property is a label-less `form-const-string` so it acts as
 * the dropdown discriminant (objectIdentifier: "type") without rendering a
 * redundant empty row in the branch body (the dropdown already shows the type).
 * The branch `label` is what the dropdown lists as the option text
 * (item.label ?? item.const in SchemaFormDropdown.tsx).
 */
const widgetBranch = (
    type: string,
    label: string,
    configProperties: Record<string, unknown>,
    configRequired?: string[],
    // Field-order control for the widget's Config sub-form
    // (operator-task-fe-dashboard-widget-form-builder-fixes #7). The engine
    // renders a form-object's properties in `order` first (in listed order),
    // then the rest alphabetically, and tucks any key in `hidden` into a
    // collapsed native "More..." <details> section
    // (SchemaForm getShownAndHiddenProperties). Primary/basic config fields go
    // in `configOrder`; secondary toggles (live/limit/…) go in `configHidden`.
    configOrder?: string[],
    configHidden?: string[],
) => ({
    element: "form-object",
    type: "object",
    additionalProperties: false,
    label,
    required: ["type", "uid"],
    // Intuitive per-widget field order (#7): Title first, then the Config
    // block, then Layout further down, then the technical UID. The const `type`
    // discriminant carries no user-editable value, so it is tucked into the
    // collapsed "More..." section rather than shown as a greyed-out field.
    order: ["title", "config", "layout", "uid"],
    hidden: ["type"],
    properties: {
        type: { element: "form-const-string", type: "string", const: type },
        uid: uidField,
        title: titleField,
        layout: layoutField,
        config: {
            element: "form-object",
            type: "object",
            additionalProperties: false,
            label: "Config",
            ...(configRequired ? { required: configRequired } : {}),
            ...(configOrder ? { order: configOrder } : {}),
            ...(configHidden ? { hidden: configHidden } : {}),
            properties: configProperties,
        },
    },
});

// --- The 13 widget-type branches (config fields per
// operator-task-dashboard-value-schema matrix) ---------------------------
const widgetBranches = [
    widgetBranch(
        "stat_tile",
        "Stat tile",
        { record_type: recordTypeField, filter: filterField, live: liveField },
        ["record_type"],
        ["record_type", "filter"],
        ["live"],
    ),
    widgetBranch(
        "single_record",
        "Single record",
        { record_type: recordTypeField, order: orderField, filter: filterField, live: liveField },
        ["record_type"],
        ["record_type", "filter", "order"],
        ["live"],
    ),
    widgetBranch(
        "record_list",
        "Record list",
        {
            record_type: recordTypeField,
            filter: filterField,
            order: orderField,
            object_template_id: objectTemplateIdField,
            columns: columnsField,
            live: liveField,
        },
        ["record_type"],
        ["record_type", "object_template_id", "columns", "filter", "order"],
        ["live"],
    ),
    widgetBranch(
        "status_strip",
        "Status strip",
        {
            record_type: customObjectOnlyRecordTypeField,
            filter: filterField,
            order: orderField,
            limit: limitField,
            live: liveField,
        },
        ["record_type"],
        ["record_type", "filter", "order"],
        ["limit", "live"],
    ),
    widgetBranch(
        "latest_n_list",
        "Latest N list",
        {
            record_type: recordTypeField,
            partition_by: partitionByField,
            n: nField,
            filter: filterField,
            limit: limitField,
            order: orderField,
            live: liveField,
        },
        ["record_type"],
        ["record_type", "partition_by", "n", "filter", "order"],
        ["limit", "live"],
    ),
    widgetBranch(
        "relation_graph",
        "Relation graph",
        {
            mode: modeField,
            // In object_template mode the filter selects object templates; in
            // custom_object mode it also seeds the custom objects unless
            // custom_object_filter is set — so it follows `mode`.
            filter: { ...filterField, record_type_field: "mode" },
            custom_object_filter: {
                element: "form-record-filter",
                type: "object",
                record_type: "CUSTOM_OBJECT",
                label: "Custom object filter",
            },
            partition_by: partitionByField,
            n: nField,
            limit: limitField,
            order: orderField,
        },
        undefined,
        ["mode", "filter", "custom_object_filter"],
        ["partition_by", "n", "limit", "order"],
    ),
    widgetBranch(
        "markdown",
        "Markdown",
        { text: { element: "form-text", type: "string", default: "", label: "Markdown text" } },
        ["text"],
    ),
    widgetBranch(
        "action_button",
        "Action button",
        {
            flow_id: flowIdField,
            label: { element: "form-optional-string", type: "string", label: "Button label" },
            input_value: { element: "form-json", type: "object", label: "Input value" },
            navigate_to_execution: navigateToExecutionField,
        },
        ["flow_id"],
        ["flow_id", "label", "input_value"],
        ["navigate_to_execution"],
    ),
    widgetBranch(
        "run_flow_with_inputs",
        "Run flow with inputs",
        {
            flow_id: flowIdField,
            submit_label: submitLabelField,
            fixed_input: { element: "form-json", type: "object", label: "Fixed input" },
            reset_on_success: resetOnSuccessField,
            success_message: successMessageField,
            navigate_to_execution: navigateToExecutionField,
        },
        ["flow_id"],
        ["flow_id", "submit_label", "fixed_input"],
        ["reset_on_success", "success_message", "navigate_to_execution"],
    ),
    widgetBranch(
        "record_form",
        "Record form",
        {
            // Only creatable record types (record_form POSTs a new record) — Margot #11.
            record_type: creatableRecordTypeField,
            object_template_id: objectTemplateIdField,
            fields: recordFormFieldsField,
            fixed: {
                element: "form-json",
                type: "object",
                label: "Fixed values",
                description:
                    'Static values merged into every created record, keyed by dotted path — e.g. `{"value.status": "new"}`. Users do not edit these; they are always applied.',
            },
            name_template: {
                element: "form-optional-string",
                type: "string",
                label: "Name template",
                description:
                    "Name for the created record. The `{timestamp}` token is replaced with the current time so generated names stay unique. Leave empty to let the backend assign a name.",
            },
            submit_label: submitLabelField,
            reset_on_success: resetOnSuccessField,
            success_message: successMessageField,
        },
        // object_template_id is required when Record type is Custom object; the
        // field only renders in that case (visibleWhen), so the required cue shows
        // exactly then (Margot 2026-09-28 #11).
        ["record_type", "object_template_id"],
        ["record_type", "object_template_id", "fields"],
        ["fixed", "name_template", "submit_label", "reset_on_success", "success_message"],
    ),
    widgetBranch(
        "execution_output",
        "Execution output",
        {
            // execution_id and flow_id are mutually exclusive — the widget shows
            // the output of ONE execution, identified either directly by id or by
            // the latest execution of a flow. Each is hidden once the other is set
            // so the user can't provide both conflicting inputs (Margot 2026-09-28
            // #7, via the SchemaForm `visibleWhen` conditional-visibility primitive).
            execution_id: {
                element: "form-optional-string",
                type: "string",
                label: "Execution id",
                visibleWhen: { field: "flow_id", isSet: false },
            },
            // optional flow reference — pick a flow by name (stores its id), same
            // record-reference control as action_button.flow_id (#3).
            flow_id: {
                element: "form-optional-autocomplete",
                record_type: "flow",
                store: "record_id",
                label: "Flow",
                default: null,
                anyOf: [{ type: "string" }, { type: "null" }],
                visibleWhen: { field: "execution_id", isSet: false },
            },
            show: showField,
            log_limit: { element: "form-optional-integer", type: "integer", label: "Log limit" },
            live: liveField,
        },
        undefined,
        ["execution_id", "flow_id", "show"],
        ["log_limit", "live"],
    ),
    widgetBranch(
        "status_tile",
        "Status tile",
        {
            record_type: recordTypeField,
            filter: filterField,
            order: orderField,
            field: {
                element: "form-string",
                type: "string",
                default: "",
                label: "Field",
                description:
                    "The field whose value selects the tile state. For custom objects this reads `value.<field>`; for other record types it is a top-level column.",
            },
            value_map: {
                element: "form-json",
                type: "object",
                label: "Value map",
                description:
                    'Map of field value → tile appearance, e.g. `{"active": {"color": "green", "label": "Active"}, "error": {"color": "red", "label": "Error"}}`. Each entry is `{color, label, detail?}`.',
            },
            default: {
                element: "form-json",
                type: "object",
                label: "Default appearance",
                description: "Fallback `{color, label, detail?}` used when the value matches no **Value map** key.",
            },
            stale: {
                element: "form-json",
                type: "object",
                label: "Stale override",
                description:
                    "Optional `{grace_seconds, skip_when_values?, color, label, detail?}` that maps a missing/old heartbeat to a dead state.",
            },
            live: liveField,
        },
        ["record_type", "field", "value_map"],
        ["record_type", "field", "value_map", "default"],
        ["filter", "order", "stale", "live"],
    ),
    widgetBranch(
        "status_matrix",
        "Status matrix",
        {
            setting_name: {
                element: "form-string",
                type: "string",
                default: "",
                label: "Setting name",
                description:
                    "Name of the SETTING record whose JSON `value` holds a pre-aggregated status matrix: groups of rows, each row with a list of status cells. Your own flow writes that setting; the widget reads and renders it. See the Dashboards documentation for the JSON shape and an example generator flow.",
            },
            groups: {
                element: "form-optional-array",
                type: "array",
                label: "Groups",
                items: { element: "form-string", type: "string", default: "", label: "Group" },
                description:
                    "Optional subset/ordering of matrix groups to show, by group `label`. When empty, all groups present in the setting are shown in their stored order.",
            },
            live: liveField,
        },
        ["setting_name"],
        ["setting_name", "groups"],
        ["live"],
    ),
];

export const dashboardWidgetSchema: SchemaFormSchema = {
    element: "form-object",
    type: "object",
    additionalProperties: false,
    required: ["widgets"],
    style: { border: false, align: "VERTICAL" },
    // Top-level field order (#7): the Widgets list is the main content and
    // renders first, then the Grid settings; the technical schema Version is
    // tucked into the collapsed "More..." section.
    order: ["widgets", "grid"],
    hidden: ["version"],
    properties: {
        version: { element: "form-optional-integer", type: "integer", default: 1, label: "Schema version" },
        grid: {
            element: "form-object",
            type: "object",
            additionalProperties: false,
            label: "Grid",
            properties: {
                columns: {
                    element: "form-optional-integer",
                    type: "integer",
                    default: 12,
                    minimum: 1,
                    label: "Columns",
                    description:
                        "How many equal columns the dashboard grid is divided into (default 12). Widgets span multiple columns via their **Layout → Column span** — 12 gives fine-grained control (a half-width widget spans 6). It is *not* 12 separate widget slots; a lower value like 2–3 only makes each column wider.",
                },
                gap: { element: "form-optional-integer", type: "integer", default: 16, label: "Gap (px)" },
                rowHeight: {
                    element: "form-optional-integer",
                    type: "integer",
                    default: 80,
                    label: "Row height (px)",
                },
            },
        },
        widgets: {
            element: "form-array",
            type: "array",
            label: "Widgets",
            itemsLabel: "widget",
            // Deleting a widget via the array item's 'x' discards its whole
            // config; require an "are you sure?" confirmation first.
            confirmDelete: true,
            // Remember which widgets the user collapsed (per dashboard, keyed by
            // widget uid) across view <-> config switches and reloads.
            rememberCollapsed: true,
            itemKeyProperty: "uid",
            items: {
                element: "form-dropdown",
                label: "Widget type",
                objectIdentifier: "type",
                defaultIndex: 0,
                oneOf: widgetBranches,
            },
        },
    },
};

export default dashboardWidgetSchema;
